import { isWhatsappLink, normalizeWhatsapp, whatsappLink } from "@/lib/whatsapp";
import catCityAsset from "@/assets/cat_city_256.png.asset.json";
import { telegramHref } from "@/lib/social-links";
import { usePublicActiveRoles, RoleBadge } from "@/lib/roles";
import { AegisBadge } from "@/components/AegisBadge";
import { SquareCover } from "@/components/SquareCover";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { createContext, useContext } from "react";
import { AppShell } from "@/components/AppShell";
import { SectionHint } from "@/components/SectionHint";
import { ExpertRequestsPlate, OrganizerChatInvite } from "@/components/OrganizerExtras";
import { cityFilterOptions } from "@/lib/filter-options";
import { setFlag } from "@/lib/first-steps-flags";
import { SiteHeader } from "@/components/landing/SiteHeader";
import { GuestPopup, GuestJoinSheet } from "@/components/GuestJoin";
import { GuestInviteBlock } from "@/components/GuestInviteBlock";
import { setRedirectIntent } from "@/lib/redirect-intent";
import { setPendingRsvp } from "@/lib/pending-rsvp";
import { sanitizePublicDescription } from "@/routes/e.$eventId";
import { mapPublicEventRow, type PublicEventFeedRow } from "@/lib/public-events";
import { ShareEventButton } from "@/components/ShareEventButton";
import { ActivityReportDialog } from "@/components/ActivityReportDialog";

import {
  PageHeader,
  ActiveFilters,
  SearchFilterRow,
  SegmentedTabs,
  SocialProof,
  EntityCard,
  NeutralPill,
  OutlinePill,
  type ProofAvatar,
} from "@/components/kit";
import { registrationMode, seatsLeft } from "@/lib/registration-mode";
import { dayCellWidth, eventDotKeys, isPastEvent, multiDayNote, multiDaySlots } from "@/lib/events-calendar";
import { useCircleNames } from "@/lib/circle-experts";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useRef, useState } from "react";
import {
  MapPin,
  Users,
  Plus,
  User as UserIcon,
  ImageIcon,
  Check,
  Award,
  ArrowUp,
  MessageCircle,
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { pluralCities, pluralMeetings, pluralParticipants, pluralSeats } from "@/lib/plural";
import { EVENT_TOPICS } from "@/lib/event-topics";
import { useAuth } from "@/lib/auth";
import { useModerationCounts } from "@/hooks/use-moderation-counts";
import { useBodyScrollLock } from "@/hooks/use-body-scroll-lock";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { TURKEY_CITIES } from "@/lib/people-constants";
import { toast } from "sonner";
import { SaveContactButton } from "@/lib/saved-contacts";
import {
  appendCodeToWhatsappMessage,
  clearPendingRsvp,
  createPendingPurchase,
  ensureRsvp,
  getPendingRsvp,
} from "@/lib/pending-rsvp";

export const Route = createFileRoute("/events")({
  ssr: false,
  validateSearch: (search: Record<string, unknown>): { event?: string } => ({
    event: typeof search.event === "string" ? search.event : undefined,
  }),
  head: () => ({
    meta: [
      { title: "Афиша событий в Турции — TurkeyDrive" },
      {
        name: "description",
        content:
          "Встречи, мастер-классы и вечеринки русскоязычного сообщества в Турции: смотри, куда сходить.",
      },
      { property: "og:type", content: "website" },
      { property: "og:title", content: "Афиша событий в Турции — TurkeyDrive" },
      {
        property: "og:description",
        content: "Встречи, мастер-классы и вечеринки русскоязычного сообщества в Турции.",
      },
      { name: "twitter:card", content: "summary" },
    ],
    links: [{ rel: "canonical", href: "https://turkey-drive.com/events" }],
  }),
  component: EventsRoute,
});

/** Гостевой режим афиши: любое действие ведёт на вход с возвратом к событию. */
type GuestMode = { guest: boolean; joinOpen?: boolean; goAuth: (eventId?: string, rsvp?: boolean) => void };
const GuestCtx = createContext<GuestMode>({ guest: false, goAuth: () => {} });
const useGuest = () => useContext(GuestCtx);

function guestGoAuth(eventId?: string, rsvp?: boolean, login?: boolean) {
  const target = eventId ? `/events?event=${eventId}` : "/events";
  if (eventId && rsvp) setPendingRsvp(eventId);
  setRedirectIntent(target);
  window.location.assign(
    `/auth?redirect=${encodeURIComponent(target)}&mode=${login ? "signin" : "signup"}`,
  );
}

function EventsRoute() {
  const { user, isLoading } = useAuth();
  const [sheet, setSheet] = useState<{ open: boolean; eventId?: string; rsvp?: boolean }>({
    open: false,
  });
  if (isLoading) return <div className="min-h-screen bg-white" />;
  if (user) {
    return (
      <AppShell>
        <EventsPage />
      </AppShell>
    );
  }
  return (
    <GuestCtx.Provider
      value={{ guest: true, joinOpen: sheet.open, goAuth: (eventId, rsvp) => setSheet({ open: true, eventId, rsvp }) }}
    >
      <div className="min-w-0 overflow-x-clip">
        <SiteHeader />
        <EventsPage />
      </div>
      <GuestJoinSheet
        open={sheet.open}
        onOpenChange={(open) => setSheet((s) => ({ ...s, open }))}
        onSignup={() => guestGoAuth(sheet.eventId, sheet.rsvp)}
        onLogin={() => guestGoAuth(sheet.eventId, sheet.rsvp, true)}
      />
    </GuestCtx.Provider>
  );
}

export type EventRow = {
  id: string;
  created_at: string | null;
  title: string;
  description: string | null;
  event_date: string | null;
  event_end_date: string | null;
  city: string;
  location_details: string | null;
  cover_image_url: string | null;
  max_attendees: number | null;
  organizer_user_id: string | null;
  status: string;
  price: string | null;
  event_type: string | null;
  audience_tag: string | null;
  ticket_url: string | null;
  co_organizer_user_id: string | null;
  co_organizer_name: string | null;
  topics: string[] | null;
  signup_via_organizer?: boolean | null;
  circle_id?: string | null;
  /** Только в гостевом режиме: данные организаторов из публичной функции. */
  organizer_name?: string | null;
  organizer_photo_url?: string | null;
  co_organizer_profile_name?: string | null;
  co_organizer_photo_url?: string | null;
  attendees_count?: number;
};

type ProfileMini = {
  user_id: string;
  full_name: string;
  photo_url: string | null;
  username: string | null;
  telegram?: string | null;
  social_links?: string[] | null;
};

type ModerationProfile = {
  user_id: string;
  full_name: string;
  photo_url: string | null;
  city: string;
  created_at: string | null;
};

const EVENT_COLS =
  "id, created_at, title, description, event_date, event_end_date, city, location_details, cover_image_url, max_attendees, organizer_user_id, status, price, event_type, audience_tag, ticket_url, co_organizer_user_id, co_organizer_name, topics, signup_via_organizer, circle_id";

function formatMemberSince(date: string | null | undefined): string {
  if (!date) return "";
  const value = new Date(date);
  if (Number.isNaN(value.getTime())) return "";
  const months = [
    "января",
    "февраля",
    "марта",
    "апреля",
    "мая",
    "июня",
    "июля",
    "августа",
    "сентября",
    "октября",
    "ноября",
    "декабря",
  ];
  return `${months[value.getMonth()]} ${value.getFullYear()}`;
}

export function formatEventPrice(raw: string | null | undefined): string | null {
  const digits = (raw ?? "").replace(/\D/g, "");
  if (!digits) return null;
  return `${String(Number(digits))} ₺`;
}

function EventBadges({ event, className = "" }: { event: EventRow; className?: string }) {
  const price = formatEventPrice(event.price);
  return (
    <div className={`flex flex-wrap items-center gap-1.5 ${className}`}>
      {event.event_type === "official" && <OutlinePill>Official</OutlinePill>}
      <NeutralPill>
        {price ? price : event.signup_via_organizer ? "Цена по запросу" : "Бесплатно"}
      </NeutralPill>
      {event.audience_tag === "family" && (
        <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-semibold text-emerald-700">
          Семейное
        </span>
      )}
      {event.audience_tag === "18+" && (
        <span className="rounded-full bg-rose-100 px-2 py-0.5 text-[10px] font-semibold text-rose-700">
          18+
        </span>
      )}
      {event.audience_tag === "women" && (
        <span className="rounded-full bg-pink-100 px-2 py-0.5 text-[10px] font-semibold text-pink-700">
          Только для женщин
        </span>
      )}
      {event.audience_tag === "men" && (
        <span className="rounded-full bg-sky-100 px-2 py-0.5 text-[10px] font-semibold text-sky-700">
          Только для мужчин
        </span>
      )}
      {(event.topics ?? []).map((t) => (
        <span
          key={t}
          className="rounded-full bg-td-teal/10 px-2 py-0.5 text-[10px] font-semibold text-td-teal"
        >
          {t}
        </span>
      ))}
    </div>
  );
}

export function formatEventDate(iso: string | null): string {
  if (!iso) return "";
  const d = new Date(iso);
  const date = d.toLocaleDateString("ru-RU", { day: "numeric", month: "long" });
  const time = d.toLocaleTimeString("ru-RU", { hour: "2-digit", minute: "2-digit" });
  return `${date}, ${time}`;
}

const LOCATION_URL_RE = /(?:https?:\/\/|www\.|(?:maps\.)?app\.goo\.gl\/|goo\.gl\/)\S+/gi;

function parseLocationDetails(details: string | null): {
  text: string | null;
  mapUrl: string | null;
} {
  if (!details) return { text: null, mapUrl: null };
  const matches = details.match(LOCATION_URL_RE) ?? [];
  const rawUrl = (matches[0] ?? "").replace(/[),.;!?]+$/, "");
  const mapUrl = rawUrl ? (/^https?:\/\//i.test(rawUrl) ? rawUrl : `https://${rawUrl}`) : null;
  const text = details
    .replace(LOCATION_URL_RE, "")
    .replace(/^[,;·\-–\s]+/, "")
    .replace(/[,;·\-–\s]+$/, "")
    .trim();
  return { text: text || null, mapUrl };
}

function formatTime(iso: string | null): string {
  if (!iso) return "";
  return new Date(iso).toLocaleTimeString("ru-RU", { hour: "2-digit", minute: "2-digit" });
}

function dayKey(iso: string | null): string {
  if (!iso) return "no-date";
  const d = new Date(iso);
  return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
}

function isMultiDay(e: EventRow): boolean {
  return !!e.event_date && !!e.event_end_date && dayKey(e.event_end_date) !== dayKey(e.event_date);
}

function isOngoing(e: EventRow): boolean {
  if (!isMultiDay(e)) return false;
  const now = Date.now();
  return new Date(e.event_date!).getTime() <= now && now < new Date(e.event_end_date!).getTime();
}

/** Дата группы: идущее многодневное событие — в группе сегодняшнего дня. */
function groupIsoOf(e: EventRow): string | null {
  return isOngoing(e) ? new Date().toISOString() : e.event_date;
}

function formatMultiDayRange(e: EventRow): string {
  const part = (iso: string) => {
    const d = new Date(iso);
    const date = d
      .toLocaleDateString("ru-RU", { day: "numeric", month: "short" })
      .replace(/\.$/, "");
    return `${date} ${formatTime(iso)}`;
  };
  return `${part(e.event_date!)} – ${part(e.event_end_date!)}`;
}

function dayLabel(iso: string | null): string {
  if (!iso) return "Без даты";
  const d = new Date(iso);
  const today = new Date();
  const tomorrow = new Date();
  tomorrow.setDate(today.getDate() + 1);
  if (dayKey(iso) === dayKey(today.toISOString())) return "Сегодня";
  if (dayKey(iso) === dayKey(tomorrow.toISOString())) return "Завтра";
  return d.toLocaleDateString("ru-RU", { weekday: "long", day: "numeric", month: "long" });
}

function groupDayLabel(iso: string | null, count: number): string {
  if (!iso) return `Без даты · ${pluralMeetings(count)}`;
  const d = new Date(iso);
  const today = new Date();
  const tomorrow = new Date(today);
  tomorrow.setDate(today.getDate() + 1);
  const date = d.toLocaleDateString("ru-RU", { day: "numeric", month: "long" });
  const prefix =
    dayKey(iso) === keyOfDate(today)
      ? "Сегодня"
      : dayKey(iso) === keyOfDate(tomorrow)
        ? "Завтра"
        : d.toLocaleDateString("ru-RU", { weekday: "long" });
  return `${prefix[0]?.toUpperCase() ?? ""}${prefix.slice(1)}, ${date} · ${pluralMeetings(count)}`;
}

function initials(name: string): string {
  return name
    .split(/\s+/)
    .slice(0, 2)
    .map((p) => p[0] ?? "")
    .join("")
    .toUpperCase();
}

type Tab = "all" | "organizing" | "going" | "moderation";

const WEEKDAYS_SHORT = ["Вс", "Пн", "Вт", "Ср", "Чт", "Пт", "Сб"];
const MONTHS_RU_SHORT = [
  "янв",
  "фев",
  "мар",
  "апр",
  "май",
  "июн",
  "июл",
  "авг",
  "сен",
  "окт",
  "ноя",
  "дек",
];

function keyOfDate(d: Date): string {
  return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
}

function DateStrip({
  days,
  activeDay,
  onSelect,
  eventDayKeys,
  stripRef,
}: {
  days: Date[];
  activeDay: string | null;
  onSelect: (key: string) => void;
  eventDayKeys: Set<string>;
  stripRef: { current: HTMLDivElement | null };
}) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const dayRefs = useRef<Record<string, HTMLButtonElement | null>>({});
  const todayKey = keyOfDate(new Date());
  const [cellW, setCellW] = useState(44);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const update = () => setCellW(dayCellWidth(el.clientWidth, 7, 4));
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    if (!activeDay) return;
    dayRefs.current[activeDay]?.scrollIntoView({
      behavior: "smooth",
      block: "nearest",
      inline: "center",
    });
  }, [activeDay]);

  return (
    <div
      ref={stripRef}
      className="sticky z-30 -mx-4 mt-3 border-b border-td-line bg-white px-4 py-2"
      style={{ top: "var(--site-header-height)" }}
    >
      <div
        ref={scrollRef}
        className="flex w-full min-w-0 snap-x gap-1 overflow-x-auto overscroll-x-contain [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      >
        {days.map((d) => {
          const k = keyOfDate(d);
          const selected = activeDay === k;
          const isToday = k === todayKey;
          const hasEvents = eventDayKeys.has(k);
          const previous = new Date(d);
          previous.setDate(d.getDate() - 1);
          const showMonth = d === days[0] || previous.getMonth() !== d.getMonth();
          return (
            <button
              key={k}
              type="button"
              ref={(el) => {
                dayRefs.current[k] = el;
              }}
              onClick={() => hasEvents && onSelect(k)}
              disabled={!hasEvents}
              aria-disabled={!hasEvents}
              style={{ width: cellW }}
              className="flex shrink-0 snap-start flex-col items-center text-td-dark"
              aria-label={`${d.toLocaleDateString("ru-RU", { weekday: "long", day: "numeric", month: "long" })}${hasEvents ? "" : ", встреч нет"}`}
            >
              <span className="h-3 text-[9px] font-medium uppercase leading-3 text-td-muted">
                {showMonth ? MONTHS_RU_SHORT[d.getMonth()] : ""}
              </span>
              <span className="mt-0.5 text-[11px] leading-none text-td-muted">
                {WEEKDAYS_SHORT[d.getDay()]}
              </span>
              <span
                className={`mt-1 flex h-9 w-9 items-center justify-center rounded-full text-[15px] font-semibold ${
                  selected
                    ? "bg-td-teal text-white"
                    : isToday
                      ? `border-2 border-td-teal ${hasEvents ? "text-td-dark" : "text-td-muted opacity-50"}`
                      : hasEvents
                        ? "text-td-dark"
                        : "text-td-muted opacity-50"
                }`}
              >
                {d.getDate()}
              </span>
              <span
                className={`mt-1 h-1.5 w-1.5 rounded-full ${hasEvents ? "bg-td-teal" : "bg-transparent"}`}
                aria-hidden
              />
            </button>
          );
        })}
      </div>
    </div>
  );
}

function EventsPage() {
  const { user } = useAuth();
  const uid = user?.id;
  const { guest, goAuth, joinOpen } = useGuest();
  useEffect(() => {
    if (uid) setFlag(uid, "events_open");
  }, [uid]);
  const myRoles = usePublicActiveRoles(uid);
  const modCounts = useModerationCounts();
  const [tab, setTab] = useState<Tab>("all");
  const [cityFilter, setCityFilterState] = useState<string>("all");
  // Город по умолчанию из профиля применяется один раз; ручной выбор его отменяет.
  const cityDefaultDoneRef = useRef(false);
  const [cityFallback, setCityFallback] = useState<string | null>(null);
  const setCityFilter = (v: string) => {
    cityDefaultDoneRef.current = true;
    setCityFallback(null);
    setCityFilterState(v);
    try {
      sessionStorage.setItem("events-city-choice", v);
    } catch {
      /* ignore */
    }
  };
  const [topicFilters, setTopicFilters] = useState<string[]>([]);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [topicDraft, setTopicDraft] = useState<string[]>([]);
  const [cityDraft, setCityDraft] = useState<string>("all");
  const [search, setSearch] = useState("");
  const [activeDay, setActiveDay] = useState<string | null>(null);
  const [showBackToTop, setShowBackToTop] = useState(false);
  const dateStripRef = useRef<HTMLDivElement>(null);
  const groupRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const programmaticScrollRef = useRef(false);
  const programmaticTimerRef = useRef<number | undefined>(undefined);

  const { event: openIdParam } = Route.useSearch();
  const openId = openIdParam ?? null;
  const navigate = useNavigate();
  const cardScrollYRef = useRef<number | null>(null);
  const previousOpenIdRef = useRef(openId);
  const setOpenId = (id: string | null) => {
    if (id && !openId) cardScrollYRef.current = window.scrollY;
    return navigate({ to: "/events", search: id ? { event: id } : {}, replace: true, resetScroll: false });
  };

  useEffect(() => {
    const closed = previousOpenIdRef.current !== null && openId === null;
    previousOpenIdRef.current = openId;
    const top = cardScrollYRef.current;
    if (!closed || top === null) return;
    let frame = 0;
    let attempts = 0;
    const restore = () => {
      attempts += 1;
      const maxScroll = document.documentElement.scrollHeight - window.innerHeight;
      if (maxScroll >= top) {
        window.scrollTo({ top, left: 0, behavior: "instant" });
        if (Math.abs(window.scrollY - top) < 1) return;
      }
      if (attempts < 10) frame = requestAnimationFrame(restore);
    };
    frame = requestAnimationFrame(restore);
    return () => cancelAnimationFrame(frame);
  }, [openId]);

  const qc = useQueryClient();

  const { data: isAdmin = false } = useQuery({
    queryKey: ["profile", "is-admin", uid],
    enabled: !!uid,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("profiles")
        .select("is_admin")
        .eq("user_id", uid!)
        .maybeSingle();
      if (error) throw error;
      return !!(data as { is_admin?: boolean } | null)?.is_admin;
    },
  });

  // Профиль текущего пользователя — общий кэш с главной («profile», uid).
  const { data: myProfile } = useQuery({
    queryKey: ["profile", uid],
    enabled: !!uid,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("profiles")
        .select("*")
        .eq("user_id", uid!)
        .single();
      if (error) throw error;
      return data as ProfileMini;
    },
  });

  const { data: pendingEvents = [], isLoading: loadingPending } = useQuery({
    queryKey: ["events", "pending-moderation"],
    enabled: isAdmin,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("events")
        .select(EVENT_COLS)
        .eq("status", "pending")
        .order("event_date", { ascending: true });
      if (error) throw error;
      return (data ?? []) as EventRow[];
    },
  });

  // Профили организаторов и соорганизаторов одним запросом на всю очередь.
  const moderationProfileIds = useMemo(
    () => [
      ...new Set(
        pendingEvents.flatMap((event) =>
          [event.organizer_user_id, event.co_organizer_user_id].filter((id): id is string =>
            Boolean(id),
          ),
        ),
      ),
    ],
    [pendingEvents],
  );
  const { data: moderationProfiles } = useQuery({
    queryKey: ["profiles", "event-moderation-authors", moderationProfileIds],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("profiles")
        .select("user_id, full_name, photo_url, city, created_at")
        .in("user_id", moderationProfileIds);
      if (error) throw error;
      return new Map(
        ((data ?? []) as ModerationProfile[]).map((profile) => [profile.user_id, profile]),
      );
    },
    enabled: isAdmin && moderationProfileIds.length > 0,
  });

  const moderate = useMutation({
    mutationFn: async ({ id, status }: { id: string; status: "published" | "rejected" }) => {
      const { error } = await supabase.from("events").update({ status }).eq("id", id);
      if (error) throw error;
      return status;
    },
    onSuccess: (status) => {
      qc.invalidateQueries({ queryKey: ["events"] });
      toast.success(status === "published" ? "Событие опубликовано" : "Событие отклонено");
    },
    onError: (e: Error) => toast.error(e.message),
  });

  useEffect(() => {
    if (!isAdmin && tab === "moderation") setTab("all");
  }, [isAdmin, tab]);

  const { data: publishedEvents = [], isLoading: loadingAll } = useQuery({
    queryKey: ["events", "published", guest ? "guest" : "member"],
    queryFn: async () => {
      if (guest) {
        const { data, error } = await supabase.rpc("public_events_feed" as never);
        if (error) throw error;
        return ((data ?? []) as PublicEventFeedRow[]).map(mapPublicEventRow) as EventRow[];
      }
      const { data, error } = await supabase
        .from("events")
        .select(EVENT_COLS)
        .eq("status", "published")
        .order("event_date", { ascending: true });
      if (error) throw error;
      return (data ?? []) as EventRow[];
    },
  });

  const { statsCount, otherCityCount } = useMemo(() => {
    const now = Date.now();
    const inCity = new Set<string>();
    const other = new Set<string>();
    for (const e of publishedEvents) {
      if (e.status && e.status !== "published") continue;
      const end = e.event_end_date ?? e.event_date;
      if (end && new Date(end).getTime() < now) continue;
      if (topicFilters.length > 0 && !(e.topics ?? []).some((t) => topicFilters.includes(t)))
        continue;
      if (cityFilter === "all" || e.city === cityFilter) inCity.add(e.id);
      else other.add(e.id);
    }
    return { statsCount: inCity.size, otherCityCount: other.size };
  }, [publishedEvents, cityFilter, topicFilters]);
  const pluralEvents = (n: number) => {
    const m10 = n % 10;
    const m100 = n % 100;
    const word =
      m10 === 1 && m100 !== 11
        ? "событие"
        : m10 >= 2 && m10 <= 4 && (m100 < 12 || m100 > 14)
          ? "события"
          : "событий";
    return `${n} ${word}`;
  };
  const statsLine = statsCount > 0 ? `${pluralEvents(statsCount)} впереди` : "";
  const { data: myOrganizing = [] } = useQuery({
    queryKey: ["events", "organizing", uid],
    enabled: !!uid,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("events")
        .select(EVENT_COLS)
        // Организатор ИЛИ соорганизатор — обе роли попадают во вкладку «Организую»
        .or(`organizer_user_id.eq.${uid},co_organizer_user_id.eq.${uid}`)
        .order("event_date", { ascending: true });
      if (error) throw error;
      return (data ?? []) as EventRow[];
    },
  });

  const pastOrganizedEvents = useMemo(
    () => myOrganizing.filter((event) => event.status === "published" && isPastEvent(event)),
    [myOrganizing],
  );
  const pastOrganizedEventIds = useMemo(
    () => pastOrganizedEvents.map((event) => event.id),
    [pastOrganizedEvents],
  );
  const contributionCities = useMemo(
    () => new Set(pastOrganizedEvents.map((event) => event.city.trim()).filter(Boolean)).size,
    [pastOrganizedEvents],
  );

  const { data: contributionRsvpCount, isLoading: loadingContributionRsvps } = useQuery({
    queryKey: ["events", "contribution-rsvps", uid, pastOrganizedEventIds],
    enabled: tab === "organizing" && pastOrganizedEventIds.length > 0,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("event_rsvps")
        .select("event_id")
        .in("event_id", pastOrganizedEventIds)
        .in("status", ["going", "paid"]);
      if (error) throw error;
      return data.length;
    },
  });

  const { data: myRsvpEventIds = [] } = useQuery({
    queryKey: ["events", "my-rsvp-ids", uid],
    enabled: !!uid,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("event_rsvps")
        .select("event_id")
        .eq("user_id", uid!)
        .in("status", ["going", "paid"]);
      if (error) throw error;
      return (data ?? []).map((r) => r.event_id) as string[];
    },
  });

  const myRsvpSet = useMemo(() => new Set(myRsvpEventIds), [myRsvpEventIds]);

  const goingEvents = useMemo(
    () => publishedEvents.filter((e) => myRsvpSet.has(e.id)),
    [publishedEvents, myRsvpSet],
  );

  const listSource: EventRow[] =
    tab === "all" ? publishedEvents : tab === "organizing" ? myOrganizing : goingEvents;

  const searchOrganizerIds = useMemo(
    () =>
      [
        ...new Set(
          listSource
            .flatMap((e) => [e.organizer_user_id, e.co_organizer_user_id])
            .filter((x): x is string => !!x),
        ),
      ].sort(),
    [listSource],
  );
  const { data: organizerNames } = useQuery({
    queryKey: ["events", "organizer-names", searchOrganizerIds.join(",")],
    enabled: !guest && searchOrganizerIds.length > 0,
    staleTime: 5 * 60_000,
    queryFn: async () => {
      const { data } = await supabase
        .from("profiles")
        .select("user_id, full_name")
        .in("user_id", searchOrganizerIds);
      return new Map((data ?? []).map((p) => [p.user_id as string, p.full_name as string]));
    },
  });
  const { data: circleNames } = useCircleNames(guest ? [] : listSource.map((e) => e.circle_id));
  const searchQ = search.trim().toLowerCase();

  const baseList = useMemo(() => {
    let arr = listSource;
    if (searchQ) {
      arr = arr.filter((e) => {
        const hay = [
          e.title,
          e.organizer_name,
          e.co_organizer_name,
          e.co_organizer_profile_name,
          e.organizer_user_id ? organizerNames?.get(e.organizer_user_id) : null,
          e.co_organizer_user_id ? organizerNames?.get(e.co_organizer_user_id) : null,
          e.circle_id ? circleNames?.get(e.circle_id) : null,
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        return hay.includes(searchQ);
      });
    }
    if (cityFilter !== "all") arr = arr.filter((e) => e.city === cityFilter);
    if (topicFilters.length > 0)
      arr = arr.filter((e) => (e.topics ?? []).some((t) => topicFilters.includes(t)));
    if (tab === "all") {
      arr = arr.filter((e) => !isPastEvent(e));
    }
    return arr;
  }, [listSource, cityFilter, topicFilters, tab, searchQ, organizerNames, circleNames]);

  const eventDayKeys = useMemo(
    () =>
      new Set(
        baseList
          .filter((e) => e.event_date)
          .flatMap((e) => {
            const sl = multiDaySlots(e.event_date, e.event_end_date);
            return sl && sl.length > 0
              ? sl.map((x) => dayKey(x.iso))
              : [dayKey(groupIsoOf(e))];
          }),
      ),
    [baseList],
  );
  const dotKeys = useMemo(() => eventDotKeys(baseList), [baseList]);

  const list = useMemo(() => {
    // Вкладки «Иду»/«Организую» — это и архив: сначала предстоящие по возрастанию,
    // затем прошедшие по убыванию (недавнее — в центре внимания).
    if (tab === "going" || tab === "organizing") {
      const key = (e: EventRow) => e.event_end_date ?? e.event_date ?? "";
      const upcoming = baseList
        .filter((e) => !isPastEvent(e))
        .sort((a, b) => key(a).localeCompare(key(b)));
      const past = baseList
        .filter((e) => isPastEvent(e))
        .sort((a, b) => key(b).localeCompare(key(a)));
      return [...upcoming, ...past];
    }
    return baseList;
  }, [baseList, tab]);

  // Разделы «Предстоящие» / «Уже прошли» — только когда есть и те, и другие.
  const showSectionHeaders =
    (tab === "going" || tab === "organizing") &&
    list.some((e) => !isPastEvent(e)) &&
    list.some((e) => isPastEvent(e));
  const pastSectionLabel = tab === "organizing" ? "Проведено" : "Уже прошли";

  // Groups by day
  const groups = useMemo(() => {
    type Group = {
      label: string;
      iso: string | null;
      items: EventRow[];
      slots: Record<string, { dayIndex: number; dayCount: number }>;
    };
    const map = new Map<string, Group>();
    const add = (gIso: string | null, e: EventRow, slot?: { dayIndex: number; dayCount: number }) => {
      const k = dayKey(gIso);
      if (!map.has(k)) map.set(k, { label: dayLabel(gIso), iso: gIso, items: [], slots: {} });
      const g = map.get(k)!;
      if (g.items.some((x) => x.id === e.id)) return;
      g.items.push(e);
      if (slot) g.slots[e.id] = slot;
    };
    for (const e of list) {
      const slots = tab === "all" ? multiDaySlots(e.event_date, e.event_end_date) : null;
      if (slots && slots.length > 0) {
        for (const sl of slots) add(sl.iso, e, sl);
      } else {
        add(groupIsoOf(e), e);
      }
    }
    const out = [...map.values()];
    if (tab === "all") {
      const t = (g: Group) => (g.iso ? new Date(g.iso).setHours(0, 0, 0, 0) : Infinity);
      out.sort((a, b) => t(a) - t(b));
    }
    return out;
  }, [list, tab]);

  const stripDays = useMemo(() => {
    const start = new Date();
    start.setHours(0, 0, 0, 0);
    const lastEventDate = baseList.reduce<Date | null>((latest, event) => {
      if (!event.event_date) return latest;
      const value = new Date(event.event_date);
      return !latest || value > latest ? value : latest;
    }, null);
    const minimumEnd = new Date(start);
    minimumEnd.setDate(start.getDate() + 13);
    const end = lastEventDate && lastEventDate > minimumEnd ? lastEventDate : minimumEnd;
    end.setHours(0, 0, 0, 0);
    const count = Math.max(14, Math.round((end.getTime() - start.getTime()) / 86_400_000) + 1);
    return Array.from({ length: count }, (_, index) => {
      const day = new Date(start);
      day.setDate(start.getDate() + index);
      return day;
    });
  }, [baseList]);

  useEffect(() => {
    if (tab !== "all") return;
    const first = groups.find((group) => group.iso)?.iso;
    if (first && (!activeDay || !eventDayKeys.has(activeDay))) setActiveDay(dayKey(first));
  }, [activeDay, eventDayKeys, groups, tab]);

  useEffect(() => {
    if (tab !== "all" || groups.length === 0) return;
    const headerHeight =
      Number.parseFloat(
        getComputedStyle(document.documentElement).getPropertyValue("--site-header-height"),
      ) || 61;
    const stripHeight = dateStripRef.current?.offsetHeight ?? 68;
    const observer = new IntersectionObserver(
      (entries) => {
        if (programmaticScrollRef.current) return;
        const visible = entries
          .filter((entry) => entry.isIntersecting)
          .sort(
            (a, b) =>
              Math.abs(a.boundingClientRect.top - headerHeight - stripHeight) -
              Math.abs(b.boundingClientRect.top - headerHeight - stripHeight),
          );
        const key = visible[0]?.target.getAttribute("data-day-key");
        if (key) setActiveDay(key);
      },
      { rootMargin: `-${headerHeight + stripHeight}px 0px -65% 0px`, threshold: [0, 1] },
    );
    for (const group of groups) {
      if (!group.iso) continue;
      const node = groupRefs.current[dayKey(group.iso)];
      if (node) observer.observe(node);
    }
    return () => observer.disconnect();
  }, [groups, tab]);

  useEffect(() => {
    const onScroll = () => setShowBackToTop(window.scrollY > window.innerHeight);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => () => window.clearTimeout(programmaticTimerRef.current), []);

  const scrollToEventDay = (requestedKey: string) => {
    const [ry, rm, rd] = requestedKey.split("-").map(Number);
    const requestedStart = new Date(ry, rm, rd).getTime();
    const groupStart = (iso: string) => {
      const [gy, gm, gd] = dayKey(iso).split("-").map(Number);
      return new Date(gy, gm, gd).getTime();
    };
    const targetGroup = groups.find(
      (group) =>
        group.iso && groupStart(group.iso) >= requestedStart && groupRefs.current[dayKey(group.iso)],
    );
    if (!targetGroup?.iso) return;
    const targetKey = dayKey(targetGroup.iso);
    const target = groupRefs.current[targetKey];
    if (!target) return;
    programmaticScrollRef.current = true;
    setActiveDay(targetKey);
    const headerHeight =
      Number.parseFloat(
        getComputedStyle(document.documentElement).getPropertyValue("--site-header-height"),
      ) || 61;
    const stripHeight = dateStripRef.current?.offsetHeight ?? 68;
    const top =
      window.scrollY + target.getBoundingClientRect().top - headerHeight - stripHeight - 12;
    window.scrollTo({ top, behavior: "smooth" });
    window.clearTimeout(programmaticTimerRef.current);
    programmaticTimerRef.current = window.setTimeout(() => {
      programmaticScrollRef.current = false;
    }, 900);
  };

  const eventIds = list.map((e) => e.id);

  const guestCounts = useMemo(() => {
    const c: Record<string, number> = {};
    if (guest) for (const e of publishedEvents) c[e.id] = e.attendees_count ?? 0;
    return c;
  }, [guest, publishedEvents]);
  const { data: summary } = useQuery({
    queryKey: ["events", "rsvp-summary", eventIds],
    enabled: !guest && eventIds.length > 0,
    queryFn: async () => {
      const { data, error } = await supabase.rpc(
        "event_rsvp_summary" as never,
        { event_ids: eventIds } as never,
      );
      if (error) throw error;
      const c: Record<string, number> = {};
      const av: Record<string, ProofAvatar[]> = {};
      for (const r of (data ?? []) as unknown as {
        event_id: string;
        total: number;
        avatars: ProofAvatar[];
      }[]) {
        c[r.event_id] = r.total;
        av[r.event_id] = r.avatars ?? [];
      }
      return { c, av };
    },
  });
  const counts: Record<string, number> = guest ? guestCounts : (summary?.c ?? {});
  const avatarsByEvent: Record<string, ProofAvatar[]> = summary?.av ?? {};

  const openEvent = useMemo(() => {
    const all = [...publishedEvents, ...myOrganizing, ...pendingEvents];
    return all.find((e) => e.id === openId) ?? null;
  }, [openId, publishedEvents, myOrganizing, pendingEvents]);

  const canSeeParticipants =
    !!openEvent &&
    !!uid &&
    (isAdmin || openEvent.organizer_user_id === uid || openEvent.co_organizer_user_id === uid);
  // Полный список участников — только организатору, соорганизатору и админу.
  const { data: openParticipants = [] } = useQuery({
    queryKey: ["events", "rsvp-users", openEvent?.id],
    enabled: canSeeParticipants,
    queryFn: async () => {
      const { data: rsvps, error } = await supabase
        .from("event_rsvps")
        .select("user_id, created_at")
        .eq("event_id", openEvent!.id)
        .in("status", ["going", "paid"])
        .order("created_at", { ascending: true });
      if (error) throw error;
      const ids = [...new Set((rsvps ?? []).map((r) => r.user_id))];
      if (!ids.length) return [] as ProfileMini[];
      const { data: profiles } = await supabase
        .from("profiles")
        .select("user_id, full_name, photo_url, username")
        .in("user_id", ids);
      const byUid = new Map((profiles ?? []).map((p) => [p.user_id, p as ProfileMini]));
      return ids.map((id) => byUid.get(id)).filter((p): p is ProfileMini => !!p);
    },
  });

  // Автозапись на бесплатное мероприятие после возврата с авторизации.
  useEffect(() => {
    if (!uid || !openEvent) return;
    if (getPendingRsvp() !== openEvent.id) return;
    if (isPaidEvent(openEvent)) {
      // Платные не оформляем автоматически — человек сам жмёт «Купить билет».
      clearPendingRsvp();
      return;
    }
    const id = openEvent.id;
    clearPendingRsvp();
    void (async () => {
      const ok = await ensureRsvp(id, uid);
      if (ok) {
        qc.invalidateQueries({ queryKey: ["events"] });
        if (isCityEvent(openEvent)) {
          toast.success("Отметили: ты идёшь");
          return;
        }
        const { data: contactProfile } = await supabase
          .from("profiles")
          .select("telegram")
          .eq("user_id", uid)
          .maybeSingle();
        if (contactProfile?.telegram?.trim()) {
          toast.success("Ты записан(а). Организатор получит уведомление и свяжется с тобой");
        } else {
          toast("Добавь Telegram в профиль — так организатор сможет с тобой связаться", {
            action: {
              label: "Добавить",
              onClick: () => navigate({ to: "/profile" }),
            },
          });
        }
      }
    })();
  }, [uid, openEvent, navigate, qc]);

  const cityOptions = useMemo(() => {
    const extra = new Set<string>();
    for (const e of listSource) {
      if (!(TURKEY_CITIES as readonly string[]).includes(e.city)) extra.add(e.city);
    }
    return [...TURKEY_CITIES, ...[...extra].sort()];
  }, [listSource]);

  // Шторка: только города с актуальными опубликованными событиями (без онлайна), с числом.
  const sheetCityOptions = useMemo(() => {
    const now = Date.now();
    const counts = new Map<string, Set<string>>();
    for (const e of publishedEvents) {
      if (e.status && e.status !== "published") continue;
      const end = e.event_end_date ?? e.event_date;
      if (end && new Date(end).getTime() < now) continue;
      const c = e.city?.trim();
      if (!c || /онлайн|online/i.test(c) || c === "Не указан") continue;
      if (!counts.has(c)) counts.set(c, new Set());
      counts.get(c)!.add(e.id);
    }
    const profileCity = (myProfile as { city?: string | null } | null | undefined)?.city?.trim();
    const sorted = [...counts.entries()]
      .map(([c, ids]) => ({ c, n: ids.size }))
      .sort((a, b) =>
        a.c === profileCity ? -1 : b.c === profileCity ? 1 : b.n - a.n || a.c.localeCompare(b.c, "ru"),
      );
    const base = cityFilterOptions(sorted.map((x) => x.c));
    return base.map((o) => {
      const hit = sorted.find((x) => x.c === o.value);
      return hit ? { ...o, count: hit.n } : o;
    });
  }, [publishedEvents, myProfile]);

  useEffect(() => {
    if (cityDefaultDoneRef.current) return;
    let stored: string | null = null;
    try {
      stored = sessionStorage.getItem("events-city-choice");
    } catch {
      /* ignore */
    }
    if (stored) {
      cityDefaultDoneRef.current = true;
      setCityFilterState(stored);
      return;
    }
    if (guest || !uid) return;
    if (!myProfile || loadingAll) return;
    cityDefaultDoneRef.current = true;
    const city = (myProfile as { city?: string | null }).city?.trim();
    if (!city || city === "Другой город" || city === "Не указан" || !cityFilterOptions(cityOptions).some((o) => o.value === city && o.value !== "all"))
      return;
    const nowIso = new Date().toISOString();
    const hasUpcoming = publishedEvents.some((e) => {
      const end = e.event_end_date ?? e.event_date;
      return e.city === city && (!end || end >= nowIso);
    });
    if (hasUpcoming) {
      setCityFilterState(city);
      return;
    }
    let seen = false;
    try {
      seen = sessionStorage.getItem("events-city-fallback-seen") === "1";
      sessionStorage.setItem("events-city-fallback-seen", "1");
    } catch {
      /* ignore */
    }
    if (!seen) setCityFallback(city);
  }, [guest, uid, myProfile, loadingAll, publishedEvents, cityOptions]);

  return (
    <div className="min-h-screen bg-white">
      <main className="mx-auto max-w-[720px] px-4 py-6 md:py-10">
        {!guest && <SectionHint hint="events" className="mb-4" />}
        {!guest && <OrganizerChatInvite className="mb-4" />}
        <PageHeader
          title="Афиша"
          subtitle={
            tab === "moderation" ? statsLine || undefined : (
              <>
                {statsLine ? `${statsLine} · ` : ""}
                <button
                  type="button"
                  onClick={() => {
                    setTopicDraft(topicFilters);
                    setCityDraft(cityFilter);
                    setFiltersOpen(true);
                  }}
                  className="font-semibold text-td-dark"
                >
                  📍 {cityFilter === "all" ? "Вся Турция" : cityFilter} ▾
                </button>
                {cityFilter !== "all" && (
                  <>
                    {" · "}
                    <button
                      type="button"
                      onClick={() => setCityFilter("all")}
                      className="font-semibold text-td-teal"
                    >
                      Вся Турция
                    </button>
                  </>
                )}
              </>
            )
          }
          action={
            <button
              type="button"
              onClick={() => (guest ? goAuth() : navigate({ to: "/events/new" }))}
              className="cta-solid inline-flex h-10 items-center rounded-full px-4 text-sm"
            >
              <Plus className="mr-1 h-4 w-4" />
              Создать
            </button>
          }
        />

        {tab !== "moderation" && (
          <div className="mt-4">
            <SearchFilterRow
              value={search}
              onChange={setSearch}
              placeholder="Поиск событий"
              filterCount={topicFilters.length}
              filtersOpen={filtersOpen}
              onFiltersOpenChange={(open) => {
                setFiltersOpen(open);
                if (open) {
                  setTopicDraft(topicFilters);
                  setCityDraft(cityFilter);
                }
              }}
              onReset={() => {
                setCityFilter("all");
                setTopicFilters([]);
                setCityDraft("all");
                setTopicDraft([]);
                setFiltersOpen(false);
              }}
              onApply={() => {
                setCityFilter(cityDraft);
                setTopicFilters(topicDraft);
                setFiltersOpen(false);
              }}
              sections={[
                {
                  key: "city",
                  title: "Город",
                  mode: "single",
                  options: sheetCityOptions,
                  value: cityDraft,
                  onChange: setCityDraft,
                },
                {
                  key: "topics",
                  title: "Темы",
                  mode: "multi",
                  options: EVENT_TOPICS.map((t) => ({ value: t, label: t })),
                  value: topicDraft,
                  onChange: setTopicDraft,
                },
              ]}
            />
          </div>
        )}

        {tab === "all" && cityFilter !== "all" && otherCityCount > 0 && (
              <button
                type="button"
                onClick={() => setCityFilter("all")}
                className="mt-2 block text-left text-[13px] text-td-muted"
              >
                Ещё {pluralEvents(otherCityCount)} в других городах ·{" "}
                <span className="font-semibold text-td-teal">показать</span>
              </button>
            )}
        <div className="mt-3">
          <SegmentedTabs<Tab>
            value={tab}
            onChange={(k) => (guest && k !== "all" ? goAuth() : setTab(k))}
            items={[
              { key: "all", label: "Все" },
              { key: "going", label: "Я иду" },
              { key: "organizing", label: "Мои" },
              ...(isAdmin
                ? [{ key: "moderation" as Tab, label: "Модерация", badge: modCounts.events }]
                : []),
            ]}
          />
        </div>

        {tab !== "moderation" && (
          <ActiveFilters
            items={[
              ...topicFilters.map((t) => ({
                key: `t-${t}`,
                label: t,
                onRemove: () => setTopicFilters((prev) => prev.filter((v) => v !== t)),
              })),
            ]}
            onReset={() => {
              setCityFilter("all");
              setTopicFilters([]);
            }}
          />
        )}

        {tab === "organizing" && !guest && <ExpertRequestsPlate className="mt-4" />}

        {tab === "organizing" && pastOrganizedEvents.length > 0 && (
          <section className="mt-4 rounded-2xl bg-gradient-to-br from-td-teal to-td-dark p-5 text-white shadow-lg">
            {/* Ряд: владелец слева, бейдж «Организатор» справа */}
            <div className="flex items-center justify-between gap-2">
              <div className="flex min-w-0 items-center gap-2">
                {myProfile?.photo_url ? (
                  <img
                    loading="lazy"
                    decoding="async"
                    src={myProfile.photo_url}
                    alt={myProfile.full_name}
                    className="h-10 w-10 shrink-0 rounded-full object-cover ring-2 ring-white/30"
                  />
                ) : (
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-white/20 text-xs font-semibold ring-2 ring-white/30">
                    {initials(myProfile?.full_name || "Я")}
                  </span>
                )}
                <span className="min-w-0 truncate text-sm font-semibold">
                  {myProfile?.full_name || ""}
                </span>
              </div>
              {myRoles[0] && (
                <span className="shrink-0">
                  <RoleBadge role={myRoles[0]} size="sm" />
                </span>
              )}
            </div>

            <h2 className="mt-3 text-base font-semibold text-white">Мой вклад в сообщество</h2>
            <p className="mt-0.5 text-xs text-white/70">
              Каждая встреча — это среда, которой раньше не было
            </p>

            {(() => {
              const showCities = contributionCities > 1;
              return (
                <div className={`mt-3 grid gap-3 ${showCities ? "grid-cols-3" : "grid-cols-2"}`}>
                  <div>
                    <div className="text-3xl font-bold leading-none text-white">
                      {pastOrganizedEvents.length}
                    </div>
                    <div className="mt-1 text-[11px] leading-tight text-white/70">
                      встреч проведено
                    </div>
                  </div>
                  <div>
                    <div className="text-3xl font-bold leading-none text-white">
                      {loadingContributionRsvps || contributionRsvpCount === undefined
                        ? "…"
                        : contributionRsvpCount}
                    </div>
                    <div className="mt-1 text-[11px] leading-tight text-white/70">
                      человек пришло
                    </div>
                  </div>
                  {showCities && (
                    <div>
                      <div className="text-3xl font-bold leading-none text-white">
                        {contributionCities}
                      </div>
                      <div className="mt-1 text-[11px] leading-tight text-white/70">
                        {pluralCities(contributionCities).replace(/^\d+\s+/, "")}
                      </div>
                    </div>
                  )}
                </div>
              );
            })()}

            <div className="mt-4 border-t border-white/15 pt-3 text-[11px] text-white/60">
              TurkeyDrive · сообщество русскоязычных в Турции
            </div>
          </section>
        )}

        {tab === "moderation" ? (
          <div className="mt-6 flex flex-col gap-3">
            {loadingPending ? (
              <EmptyBox>Загрузка…</EmptyBox>
            ) : pendingEvents.length === 0 ? (
              <EmptyBox>Нет событий на модерации.</EmptyBox>
            ) : (
              pendingEvents.map((e) => (
                <div
                  key={e.id}
                  role="button"
                  tabIndex={0}
                  onClick={() => setOpenId(e.id)}
                  onKeyDown={(ev) => {
                    if (ev.key === "Enter" || ev.key === " ") {
                      ev.preventDefault();
                      setOpenId(e.id);
                    }
                  }}
                  className="cursor-pointer rounded-2xl border border-td-line bg-white p-4 transition hover:border-td-teal/60"
                >
                  <div className="mb-3 flex items-start justify-between gap-2">
                    {e.organizer_user_id && moderationProfiles?.has(e.organizer_user_id) ? (
                      (() => {
                        const organizer = moderationProfiles.get(e.organizer_user_id);
                        if (!organizer) return null;
                        const since = formatMemberSince(organizer.created_at);
                        return (
                          <div className="min-w-0">
                            <div className="flex min-w-0 items-center gap-2">
                              {organizer.photo_url ? (
                                <img
                                  loading="lazy"
                                  decoding="async"
                                  src={organizer.photo_url}
                                  alt={organizer.full_name}
                                  className="h-9 w-9 shrink-0 rounded-full object-cover"
                                />
                              ) : (
                                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-td-soft text-xs font-semibold text-td-muted">
                                  {organizer.full_name.slice(0, 1).toUpperCase()}
                                </span>
                              )}
                              <div className="min-w-0">
                                <Link
                                  to="/people/$userId"
                                  params={{ userId: organizer.user_id }}
                                  className="text-sm font-semibold text-td-dark underline-offset-2 hover:underline"
                                  onClick={(event) => event.stopPropagation()}
                                >
                                  {organizer.full_name}
                                </Link>
                                <p className="text-xs text-td-muted">
                                  {[organizer.city, since ? `в сообществе с ${since}` : null]
                                    .filter(Boolean)
                                    .join(" · ")}
                                </p>
                              </div>
                            </div>
                            {e.co_organizer_user_id && (
                              <p className="mt-1 pl-11 text-xs text-td-muted">
                                Соорганизатор:{" "}
                                {moderationProfiles?.has(e.co_organizer_user_id) ? (
                                  <Link
                                    to="/people/$userId"
                                    params={{ userId: e.co_organizer_user_id }}
                                    className="font-medium text-td-dark underline-offset-2 hover:underline"
                                    onClick={(event) => event.stopPropagation()}
                                  >
                                    {moderationProfiles.get(e.co_organizer_user_id)?.full_name}
                                  </Link>
                                ) : (
                                  "Профиль не найден"
                                )}
                              </p>
                            )}
                          </div>
                        );
                      })()
                    ) : (
                      <span className="text-xs text-td-muted">Профиль не найден</span>
                    )}
                    {e.created_at && (
                      <span className="shrink-0 text-xs text-td-muted">
                        {new Date(e.created_at).toLocaleDateString("ru-RU", {
                          day: "numeric",
                          month: "long",
                        })}
                      </span>
                    )}
                  </div>
                  <div className="flex items-start gap-3">
                    <CoverOrPlaceholder
                      url={e.cover_image_url}
                      title={e.title}
                      className="h-16 w-24 shrink-0 rounded-lg"
                    />
                    <div className="min-w-0 flex-1">
                      <h3 className="truncate font-semibold text-td-dark">{e.title}</h3>
                      <p className="mt-0.5 text-xs text-td-muted">
                        {dayLabel(e.event_date)} · {e.city}
                      </p>
                      <EventBadges event={e} className="mt-2" />
                    </div>
                  </div>
                  {e.description && (
                    <p className="mt-3 line-clamp-3 text-sm text-td-muted">{e.description}</p>
                  )}
                  <div className="mt-3 flex gap-2">
                    <Button
                      size="sm"
                      disabled={moderate.isPending}
                      onClick={(ev) => {
                        ev.stopPropagation();
                        moderate.mutate({ id: e.id, status: "published" });
                      }}
                      className="bg-td-teal text-white hover:bg-td-teal/90"
                    >
                      Опубликовать
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={moderate.isPending}
                      onClick={(ev) => {
                        ev.stopPropagation();
                        moderate.mutate({ id: e.id, status: "rejected" });
                      }}
                    >
                      Отклонить
                    </Button>
                  </div>
                </div>
              ))
            )}
          </div>
        ) : (
          <>
            {tab === "all" && (
              <DateStrip
                days={stripDays}
                activeDay={activeDay}
                onSelect={scrollToEventDay}
                eventDayKeys={dotKeys}
                stripRef={dateStripRef}
              />
            )}

            {!guest && tab === "all" && cityFallback && (
              <div className="mb-4 flex items-start justify-between gap-3 rounded-2xl border border-border bg-muted/40 px-4 py-3 text-sm text-foreground">
                <span>В {cityFallback} пока нет событий, вот что идёт по Турции</span>
                <button
                  type="button"
                  aria-label="Закрыть"
                  onClick={() => setCityFallback(null)}
                  className="-m-2 p-2 text-muted-foreground"
                >
                  ✕
                </button>
              </div>
            )}

            {/* List */}
            <div className="mt-4 flex flex-col gap-6">
              {loadingAll && tab === "all" ? (
                <EmptyBox>Загрузка…</EmptyBox>
              ) : groups.length === 0 &&
                tab === "all" &&
                cityFilter !== "all" &&
                otherCityCount > 0 ? null : groups.length === 0 ? (
                <EmptyBox>
                  {tab === "all" && "Пока нет запланированных событий, загляни позже."}
                  {tab === "organizing" && "Ты ещё не создавал(а) событий. Нажми «Создать»."}
                  {tab === "going" && "Ты пока не записан(а) ни на одно событие."}
                </EmptyBox>
              ) : (
                groups.map((g, idx) => {
                  const firstUpcomingIdx = groups.findIndex((x) => !isPastEvent(x.items[0]));
                  const firstPastIdx = groups.findIndex((x) => isPastEvent(x.items[0]));
                  const groupIsPast = isPastEvent(g.items[0]);
                  const sectionHeader = !showSectionHeaders
                    ? null
                    : !groupIsPast && idx === firstUpcomingIdx
                      ? "Предстоящие"
                      : groupIsPast && idx === firstPastIdx
                        ? pastSectionLabel
                        : null;
                  return (
                    <div
                      key={g.label + g.iso}
                      ref={(node) => {
                        if (g.iso) groupRefs.current[dayKey(g.iso)] = node;
                      }}
                      data-day-key={g.iso ? dayKey(g.iso) : undefined}
                      className="flex scroll-mt-36 flex-col gap-3"
                    >
                      {sectionHeader && (
                        <h2
                          className={
                            groupIsPast
                              ? "border-t border-td-line pt-5 font-sans text-[17px] font-semibold text-td-dark"
                              : "font-sans text-[17px] font-semibold text-td-dark"
                          }
                        >
                          {sectionHeader}
                        </h2>
                      )}
                      <h2 className="font-sans text-[15px] font-semibold text-td-dark">
                        {tab === "all" ? groupDayLabel(g.iso, g.items.length) : g.label}
                      </h2>
                      {g.items.map((e) => (
                        <EventCard
                          key={`${e.id}-${g.iso ? dayKey(g.iso) : "x"}`}
                          dayIndex={g.slots[e.id]?.dayIndex}
                          dayCount={g.slots[e.id]?.dayCount}
                          event={e}
                          count={counts[e.id] ?? 0}
                          isGoing={myRsvpSet.has(e.id)}
                          avatars={avatarsByEvent[e.id] ?? []}
                          isPast={tab !== "all" && isPastEvent(e)}
                          onOpen={() => setOpenId(e.id)}
                        />
                      ))}
                    </div>
                  );
                })
              )}
            </div>
            {guest && tab === "all" && (
              <GuestInviteBlock
                className="mt-6"
                onSignup={() => guestGoAuth()}
                onLogin={() => guestGoAuth(undefined, false, true)}
              />
            )}
            {tab === "all" && cityFilter !== "all" && otherCityCount > 0 && (
              <button
                type="button"
                onClick={() => setCityFilter("all")}
                className="mt-4 block text-left text-[13px] text-td-muted"
              >
                Ещё {pluralEvents(otherCityCount)} в других городах ·{" "}
                <span className="font-semibold text-td-teal">показать</span>
              </button>
            )}
          </>
        )}
      </main>

      {guest && <GuestPopup
        allTab={tab === "all"}
        eventId={openId}
        filtersOpen={filtersOpen}
        joinOpen={!!joinOpen}
        onSignup={() => guestGoAuth()}
        onLogin={() => guestGoAuth(undefined, false, true)}
      />}

      <EventDialog
        event={openEvent}
        count={openEvent ? (counts[openEvent.id] ?? 0) : 0}
        isGoing={openEvent ? myRsvpSet.has(openEvent.id) : false}
        participants={openParticipants}
        avatars={openEvent ? (avatarsByEvent[openEvent.id] ?? []) : []}
        canSeeParticipants={canSeeParticipants}
        canEdit={!!openEvent && (isAdmin || (!!uid && openEvent.organizer_user_id === uid))}
        canModerate={isAdmin && openEvent?.status === "pending"}
        moderating={moderate.isPending}
        onModerate={(status) => {
          if (!openEvent) return;
          moderate.mutate({ id: openEvent.id, status }, { onSuccess: () => setOpenId(null) });
        }}
        onClose={() => setOpenId(null)}
      />

      {showBackToTop && (
        <Button
          type="button"
          size="icon"
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          className="fixed bottom-24 right-4 z-30 h-12 w-12 rounded-full bg-td-teal text-white shadow-lg hover:bg-td-teal/90 md:bottom-6 md:right-6"
          aria-label="Вернуться наверх"
        >
          <ArrowUp className="h-5 w-5" aria-hidden="true" />
        </Button>
      )}
    </div>
  );
}

function EmptyBox({ children }: { children: React.ReactNode }) {
  return (
    <div className="rounded-2xl border border-td-line bg-white p-8 text-center text-td-muted">
      {children}
    </div>
  );
}

function CoverOrPlaceholder({
  url,
  title,
  className,
}: {
  url: string | null;
  title: string;
  className?: string;
}) {
  if (url) {
    return <SquareCover url={url} title={title} className={className} />;
  }
  return (
    <div
      className={
        (className ?? "") +
        " flex items-center justify-center bg-gradient-to-br from-td-teal/20 via-td-soft to-td-teal/10"
      }
    >
      <ImageIcon className="h-8 w-8 text-td-teal/60" />
    </div>
  );
}

function AvatarSmall({ p, size = 24 }: { p: ProfileMini; size?: number }) {
  const s = { width: size, height: size };
  if (p.photo_url) {
    return (
      <img
        loading="lazy"
        decoding="async"
        src={p.photo_url}
        alt={p.full_name}
        style={s}
        className="rounded-full object-cover ring-2 ring-white"
      />
    );
  }
  return (
    <div
      style={s}
      className="flex items-center justify-center rounded-full bg-td-soft text-[10px] font-semibold text-td-teal ring-2 ring-white"
    >
      {initials(p.full_name)}
    </div>
  );
}

function isPaidEvent(event: EventRow): boolean {
  return registrationMode(event) === "organizer";
}

function hasTickets(event: EventRow): boolean {
  return isPaidEvent(event) && !!event.ticket_url?.trim();
}

function seatsLabel(event: EventRow, count: number): string {
  if (isPaidEvent(event)) {
    return count > 0 ? pluralParticipants(count) : "";
  }

  if (event.max_attendees) {
    const remaining = Math.max(0, event.max_attendees - count);
    return remaining === 0 ? "Мест нет" : `Осталось ${pluralSeats(remaining)}`;
  }
  return pluralParticipants(count);
}

function useEventOrganizers(event: EventRow | null) {
  const ids = [event?.organizer_user_id, event?.co_organizer_user_id].filter(
    (v): v is string => !!v,
  );
  const { guest } = useGuest();
  const { data } = useQuery({
    queryKey: ["events", "organizers", ids.join(",")],
    enabled: !guest && ids.length > 0,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("profiles")
        .select("user_id, full_name, photo_url, username, telegram, social_links")
        .in("user_id", ids);
      if (error) throw error;
      return (data ?? []) as ProfileMini[];
    },
  });
  if (guest && event) {
    const mk = (
      id: string | null,
      name: string | null | undefined,
      photo: string | null | undefined,
    ): ProfileMini | null =>
      id && name
        ? { user_id: id, full_name: name, photo_url: photo ?? null, username: null }
        : null;
    return {
      organizer: mk(event.organizer_user_id, event.organizer_name, event.organizer_photo_url),
      coOrganizer: mk(
        event.co_organizer_user_id,
        event.co_organizer_profile_name,
        event.co_organizer_photo_url,
      ),
    };
  }
  const byId = new Map((data ?? []).map((p) => [p.user_id, p]));
  return {
    organizer: event?.organizer_user_id ? (byId.get(event.organizer_user_id) ?? null) : null,
    coOrganizer: event?.co_organizer_user_id
      ? (byId.get(event.co_organizer_user_id) ?? null)
      : null,
  };
}

function PersonInline({ p, size = 20 }: { p: ProfileMini; size?: number }) {
  const { guest } = useGuest();
  if (guest) {
    return (
      <span className="inline-flex min-w-0 max-w-full items-center gap-1.5 text-td-dark">
        <AvatarSmall p={p} size={size} />
        <span className="min-w-0 break-words font-medium [overflow-wrap:anywhere]">
          {p.full_name}
        </span>
      </span>
    );
  }
  return (
    <Link
      to="/people/$userId"
      params={{ userId: p.user_id }}
      onClick={(e) => e.stopPropagation()}
      className="inline-flex min-w-0 max-w-full items-center gap-1.5 text-td-dark hover:text-td-teal"
    >
      <AvatarSmall p={p} size={size} />
      <span className="min-w-0 break-words font-medium [overflow-wrap:anywhere]">
        {p.full_name}
      </span>
    </Link>
  );
}

function organizerTelegramLink(profile: ProfileMini): string | null {
  return telegramHref(profile.telegram);
}

function organizerInstagramLink(profile: ProfileMini): string | null {
  const value = (profile.social_links ?? [])
    .find((link) => link.toLowerCase().includes("instagram"))
    ?.trim();
  if (!value) return null;
  const handle = value
    .replace(/^https?:\/\/(?:www\.)?instagram\.com\//i, "")
    .replace(/^@/, "")
    .split(/[/?#]/)[0];
  return handle ? `https://instagram.com/${handle}` : null;
}

function organizerWhatsappLink(profile: ProfileMini): string | null {
  const value = (profile.social_links ?? []).find((link) => !!link?.trim() && isWhatsappLink(link));
  const digits = value ? normalizeWhatsapp(value) : null;
  return digits ? whatsappLink(digits) : null;
}

const contactPillCls =
  "inline-flex h-7 max-w-full shrink-0 items-center gap-1 whitespace-nowrap rounded-full border border-td-teal/40 px-2.5 text-[11px] font-medium text-td-teal";

function OrganizerContactLinks({ organizer }: { organizer: ProfileMini }) {
  const telegramLink = organizerTelegramLink(organizer);
  const whatsappHref = telegramLink ? null : organizerWhatsappLink(organizer);
  const instagramLink = telegramLink || whatsappHref ? null : organizerInstagramLink(organizer);

  return (
    <div className="flex min-w-0 flex-wrap items-center gap-2">
      {telegramLink && (
        <a
          href={telegramLink}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(event) => event.stopPropagation()}
          className={contactPillCls}
        >
          <MessageCircle className="h-3 w-3 shrink-0" />
          Задать вопрос организатору
        </a>
      )}
      {whatsappHref && (
        <a
          href={whatsappHref}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(event) => event.stopPropagation()}
          className={contactPillCls}
        >
          <MessageCircle className="h-3 w-3 shrink-0" />
          Задать вопрос организатору
        </a>
      )}
      {instagramLink && (
        <a
          href={instagramLink}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(event) => event.stopPropagation()}
          className={contactPillCls}
        >
          <MessageCircle className="h-3 w-3 shrink-0" />
          Задать вопрос организатору
        </a>
      )}
      <Link
        to="/people/$userId"
        params={{ userId: organizer.user_id }}
        onClick={(event) => event.stopPropagation()}
        className="text-xs font-medium text-td-teal hover:underline"
      >
        Профиль организатора
      </Link>
    </div>
  );
}

function OrganizerLines({
  event,
  size = 20,
  showContacts = true,
}: {
  event: EventRow;
  size?: number;
  showContacts?: boolean;
}) {
  const { organizer, coOrganizer } = useEventOrganizers(event);
  const { user } = useAuth();
  const { guest } = useGuest();
  const coName = event.co_organizer_name?.trim();
  const isOrganizer =
    !!user && (event.organizer_user_id === user.id || event.co_organizer_user_id === user.id);
  if (isCityEvent(event) && coName) {
    return (
      <div className="min-w-0 break-words text-xs text-td-muted [overflow-wrap:anywhere]">
        Организатор: <span className="font-medium text-td-dark">{coName}</span>
      </div>
    );
  }
  return (
    <div className="flex min-w-0 flex-col gap-1 text-xs text-td-muted">
      <div className="inline-flex min-w-0 flex-wrap items-center gap-1.5">
        <span>Организатор:</span>
        {organizer ? (
          <PersonInline p={organizer} size={size} />
        ) : (
          <span className="font-medium text-td-dark">Организатор TurkeyDrive</span>
        )}
      </div>
      {(coOrganizer || coName) && (
        <div className="inline-flex min-w-0 flex-wrap items-center gap-1.5">
          <span>Соорганизатор:</span>
          {coOrganizer ? (
            <PersonInline p={coOrganizer} size={size} />
          ) : (
            <span className="min-w-0 break-words font-medium text-td-dark [overflow-wrap:anywhere]">
              {coName}
            </span>
          )}
        </div>
      )}
      {organizer && showContacts && !guest && !isOrganizer && (
        <OrganizerContactLinks organizer={organizer} />
      )}
    </div>
  );
}

function organizerContactOf(organizer: ProfileMini | null): {
  url: string;
  channel: "Telegram" | "WhatsApp" | "Instagram";
} | null {
  if (!organizer) return null;
  const tg = organizerTelegramLink(organizer);
  if (tg) return { url: tg, channel: "Telegram" };
  const wa = organizerWhatsappLink(organizer);
  if (wa) return { url: wa, channel: "WhatsApp" };
  const ig = organizerInstagramLink(organizer);
  return ig ? { url: ig, channel: "Instagram" } : null;
}

/** Цена так же, как в шапке события. */
function eventPriceLabel(event: EventRow): string | null {
  const price = formatEventPrice(event.price);
  if (price) return price;
  return event.signup_via_organizer ? "Цена по запросу" : null;
}

function formatIstanbulDate(iso: string | null): string {
  if (!iso) return "";
  const d = new Date(iso);
  const date = d.toLocaleDateString("ru-RU", {
    day: "numeric",
    month: "long",
    timeZone: "Europe/Istanbul",
  });
  const time = d.toLocaleTimeString("ru-RU", {
    hour: "2-digit",
    minute: "2-digit",
    timeZone: "Europe/Istanbul",
  });
  return `${date}, ${time}`;
}

function organizerSignupMessage(
  event: EventRow,
  myName: string | null,
  code: string | null,
): string {
  const intro = myName?.trim()
    ? `Здравствуйте! Я ${myName.trim()}, резидент TurkeyDrive.`
    : "Здравствуйте! Я резидент TurkeyDrive.";
  const parts = [`«${event.title}»`, formatIstanbulDate(event.event_date), event.city].filter(
    Boolean,
  );
  return `${intro} Хочу записаться на ${parts.join(", ")}.${code ? ` (код: ${code})` : ""}`;
}

function withTextParam(url: string, text: string): string {
  return `${url}${url.includes("?") ? "&" : "?"}text=${encodeURIComponent(text)}`;
}

function SignupPaymentSection({ event }: { event: EventRow }) {
  const { organizer } = useEventOrganizers(event);
  const mode = event.ticket_url?.trim()
    ? "ticket"
    : organizerContactOf(organizer)
      ? "organizer"
      : "profile";
  const price = eventPriceLabel(event);
  const contact = organizerContactOf(organizer);
  const name = organizer?.full_name?.trim() || "организатором";
  const hint =
    mode === "ticket"
      ? "Нажми «Записаться у организатора»: откроется чат организатора, где можно оплатить и подтвердить запись."
      : mode === "organizer" && contact
        ? `Нажми «Записаться у организатора»: откроется чат с ${name} в ${contact.channel} с готовым сообщением. Организатор подтвердит запись и пришлёт, как оплатить. Место подтверждается после оплаты.`
        : "Нажми «Записаться у организатора»: откроется профиль организатора, там его контакты.";
  return (
    <Section title="Запись и оплата">
      {price && <div className="text-lg font-bold text-td-dark">{price}</div>}
      {event.max_attendees ? (
        <div className="text-sm text-td-dark">Мест: {event.max_attendees}</div>
      ) : null}
      <p className="mt-1 break-words text-sm text-td-muted [overflow-wrap:anywhere]">{hint}</p>
    </Section>
  );
}

/** Городское событие: без организатора-пользователя. */
function isCityEvent(event: EventRow): boolean {
  return !event.organizer_user_id;
}

/** Положительная числовая цена → «Купить билет», иначе «Сайт организатора». */
function cityTicketLabel(price: string | null | undefined): string {
  const nums = (price ?? "").replace(/\s/g, "").match(/\d+(?:[.,]\d+)?/g) ?? [];
  return nums.some((n) => parseFloat(n.replace(",", ".")) > 0) ? "Купить билет" : "Сайт организатора";
}

const CHAT_LINK_RE = /(wa\.me|t\.me|instagram\.com)/i;

function CityEventAction({
  event,
  isGoing,
  sizeCls,
  wrapCls,
  block,
}: {
  event: EventRow;
  isGoing: boolean;
  sizeCls: string;
  wrapCls: string;
  block?: boolean;
}) {
  const { guest, goAuth } = useGuest();
  const hasTicket = !!event.ticket_url?.trim();
  const buy = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!guest) {
      window.open(event.ticket_url!.trim(), "_blank", "noopener,noreferrer");
      return;
    }
    // Окно открываем синхронно, до await — иначе Safari блокирует попап.
    const win = window.open("", "_blank");
    const { data, error } = await supabase.rpc("public_event", { event_id: event.id });
    const row = Array.isArray(data) ? data[0] : data;
    const url = (row as { ticket_url?: string | null } | null)?.ticket_url?.trim();
    if (error || !url) {
      win?.close();
      toast.error("Не удалось открыть ссылку, попробуй позже");
      return;
    }
    if (CHAT_LINK_RE.test(url)) {
      win?.close();
      goAuth(event.id);
      return;
    }
    if (win) win.location.href = url;
    else window.location.assign(url);
  };
  const btnCls = block
    ? `${sizeCls.replace(" w-full", "")} flex-1 min-w-0 justify-center text-center leading-tight whitespace-normal`
    : `shrink-0 whitespace-nowrap ${sizeCls}`;
  return (
    <div className={wrapCls}>
      <div className={`flex min-w-0 items-center gap-2 ${block ? "flex-wrap" : ""}`}>
        {hasTicket && (
          <button
            type="button"
            onClick={(e) => void buy(e)}
            className={`inline-flex items-center rounded-full border border-td-teal bg-white font-semibold text-td-teal transition hover:bg-td-teal/10 ${btnCls}`}
          >
            {cityTicketLabel(event.price)}
          </button>
        )}
        {guest ? (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              goAuth(event.id, true);
            }}
            className={`inline-flex items-center rounded-full bg-td-teal font-semibold text-white hover:bg-td-teal/90 ${btnCls}`}
          >
            Я иду
          </button>
        ) : (
          <RsvpButton
            eventId={event.id}
            isGoing={isGoing}
            full={false}
            sizeCls={block ? btnCls.replace("whitespace-normal", "") : sizeCls}
            cityEvent
          />
        )}
      </div>
    </div>
  );
}

function EventAction({
  event,
  count,
  isGoing,
  compact,
  block,
}: {
  event: EventRow;
  count: number;
  isGoing: boolean;
  compact?: boolean;
  block?: boolean;
  tealRsvp?: boolean;
  onOpenDetails?: () => void;
}) {
  const sizeCls = `${compact ? "h-11 px-4 text-[13px]" : "h-11 px-5 text-sm"}${block ? " w-full justify-center text-center" : ""}`;
  const wrapCls = block ? "w-full min-w-0" : "min-w-0 max-w-full shrink-0";
  const { user } = useAuth();
  const [manualUrl, setManualUrl] = useState<string | null>(null);
  const { organizer } = useEventOrganizers(event);
  const mode = registrationMode(event);
  const { guest, goAuth } = useGuest();
  const outlineCls = `inline-flex shrink-0 items-center whitespace-nowrap rounded-full border border-td-teal bg-white font-semibold text-td-teal transition hover:bg-td-teal/10 ${sizeCls}`;
  const label = "Записаться у организатора";
  const shortLabel = label;

  if (isCityEvent(event)) {
    return <CityEventAction event={event} isGoing={isGoing} sizeCls={sizeCls} wrapCls={wrapCls} block={block} />;
  }

  if (guest) {
    return (
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          goAuth(event.id, mode === "in_app");
        }}
        className={
          mode === "in_app"
            ? `inline-flex shrink-0 items-center whitespace-nowrap rounded-full bg-td-teal font-semibold text-white hover:bg-td-teal/90 ${sizeCls}`
            : outlineCls
        }
      >
        {mode === "in_app" ? "Я иду" : shortLabel}
      </button>
    );
  }

  if (mode === "organizer") {
    const ticket = event.ticket_url?.trim() || null;
    const contact = organizerContactOf(organizer);
    const orgName = organizer?.full_name?.trim() || "организатором";
    const handle = async (e: React.MouseEvent) => {
      e.stopPropagation();
      if (!ticket && !contact) return; // ведёт ссылка на профиль ниже
      // Окно открываем синхронно, до await — иначе Safari молча блокирует попап.
      const win = window.open("", "_blank");
      let finalUrl: string;
      if (ticket) {
        const code = user ? await createPendingPurchase(event.id, user.id) : null;
        finalUrl = appendCodeToWhatsappMessage(ticket, code);
      } else {
        let code: string | null = null;
        let myName: string | null = null;
        if (user) {
          const [c, me] = await Promise.all([
            createPendingPurchase(event.id, user.id),
            supabase.from("profiles").select("full_name").eq("user_id", user.id).maybeSingle(),
          ]);
          code = c;
          myName = (me.data?.full_name as string | null) ?? null;
        }
        const text = organizerSignupMessage(event, myName, code);
        finalUrl = contact!.url;
        if (contact!.channel === "Telegram") {
          finalUrl = withTextParam(contact!.url, text);
        } else {
          try {
            await navigator.clipboard.writeText(text);
            toast.success("Текст скопирован, вставь его в чат");
          } catch {
            /* буфер недоступен — не мешаем открыть чат */
          }
        }
        toast(
          `Открываю чат с ${orgName}. Отправь сообщение с кодом: место подтвердится после оплаты`,
        );
      }
      if (win) {
        win.location.href = finalUrl;
      } else {
        setManualUrl(finalUrl);
        toast.info(
          "Браузер заблокировал открытие. Нажми ссылку ниже, чтобы написать организатору.",
        );
      }
    };
    return (
      <div className={wrapCls}>
        {!ticket && !contact && organizer ? (
          <Link
            to="/people/$userId"
            params={{ userId: organizer.user_id }}
            onClick={(e) => e.stopPropagation()}
            className={outlineCls}
          >
            {shortLabel}
          </Link>
        ) : (
          <button type="button" onClick={(e) => void handle(e)} className={outlineCls}>
            {shortLabel}
          </button>
        )}
        {manualUrl && (
          <a
            href={manualUrl}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            className="mt-2 block max-w-full whitespace-normal break-words rounded-full border border-td-teal px-3 py-1.5 text-center text-xs font-semibold text-td-teal [overflow-wrap:anywhere]"
          >
            Открыть чат вручную
          </a>
        )}
      </div>
    );
  }

  const left = seatsLeft(event.max_attendees, count);
  return <RsvpButton eventId={event.id} isGoing={isGoing} full={left === 0} sizeCls={sizeCls} />;
}

function cardTimeLabel(event: EventRow): string {
  if (!event.event_date) return "";
  const range = `${formatTime(event.event_date)}${event.event_end_date ? `–${formatTime(event.event_end_date)}` : ""}`;
  if (!isMultiDay(event)) return range;
  const a = new Date(event.event_date);
  const b = new Date(event.event_end_date!);
  const mon = (d: Date) => MONTHS_RU_SHORT[d.getMonth()];
  const days =
    a.getMonth() === b.getMonth() && a.getFullYear() === b.getFullYear()
      ? `${a.getDate()}–${b.getDate()} ${mon(a)}`
      : `${a.getDate()} ${mon(a)} – ${b.getDate()} ${mon(b)}`;
  return `${days} · ${range}`;
}

function CardOrganizer({ event }: { event: EventRow }) {
  const { organizer } = useEventOrganizers(event);
  const cityName = isCityEvent(event) ? event.co_organizer_name?.trim() : "";
  const name = cityName || organizer?.full_name?.trim() || "Turkey Drive";
  const hasCo = !cityName && (!!event.co_organizer_user_id || !!event.co_organizer_name?.trim());
  return (
    <span className="flex min-w-0 items-center gap-1.5">
      {isCityEvent(event) ? (
        <img src={catCityAsset.url} alt="MrDrive" className="h-5 w-5 shrink-0 rounded-full object-cover" />
      ) : organizer ? (
        <AvatarSmall p={organizer} size={20} />
      ) : (
        <span className="h-5 w-5 shrink-0 rounded-full bg-td-soft" aria-hidden />
      )}
      <span className="min-w-0 truncate">
        {name}
        {hasCo ? " и ещё 1" : ""}
      </span>
    </span>
  );
}

/** Подпись соцдоказательства: «12 идут» / «8 участников». */
function proofLabel(event: EventRow, count: number): string {
  if (isCityEvent(event) || registrationMode(event) === "in_app") {
    const m10 = count % 10,
      m100 = count % 100;
    const word = m10 === 1 && m100 !== 11 ? "идёт" : "идут";
    return `${count} ${word}`;
  }
  return pluralParticipants(count);
}

function EventCard({
  event,
  count,
  isGoing,
  avatars,
  isPast = false,
  onOpen,
  dayIndex,
  dayCount,
}: {
  dayIndex?: number;
  dayCount?: number;
  event: EventRow;
  count: number;
  isGoing: boolean;
  avatars: ProofAvatar[];
  isPast?: boolean;
  onOpen: () => void;
}) {
  const [photoDialogOpen, setPhotoDialogOpen] = useState(false);
  const { guest } = useGuest();
  const isPending = event.status === "pending";
  const { text: placeText } = parseLocationDetails(event.location_details);
  const price = isPaidEvent(event) ? (eventPriceLabel(event) ?? "Платно") : "Бесплатно";
  const city = isCityEvent(event);
  const left =
    !city && registrationMode(event) === "in_app" ? seatsLeft(event.max_attendees, count) : null;
  return (
    <>
      <EntityCard
        onOpen={onOpen}
        dimmed={isPast}
        image={
          <CoverOrPlaceholder
            url={event.cover_image_url}
            title={event.title}
            className="h-full w-full"
          />
        }
        corner={<ShareEventButton eventId={event.id} title={event.title} variant="icon" />}
        meta={
          <>
            <span className="min-w-0 truncate">{cardTimeLabel(event)}</span>
            {dayIndex && dayCount && event.event_end_date && (
              <span className="shrink-0 text-[12px] text-td-muted">
                {multiDayNote(dayIndex, dayCount, event.event_end_date)}
              </span>
            )}
            <NeutralPill>{price}</NeutralPill>
            {isOngoing(event) && <OutlinePill>Идёт сейчас</OutlinePill>}
            {isPast && <NeutralPill>Прошло</NeutralPill>}
            {isPending && <NeutralPill>На модерации</NeutralPill>}
            {event.event_type === "official" && <OutlinePill>Official</OutlinePill>}
          </>
        }
        title={event.title}
        author={<CardOrganizer event={event} />}
        place={[event.city, placeText].filter(Boolean).join(" · ")}
        proof={
          <div className="flex min-w-0 flex-col gap-0.5">
            <SocialProof
              count={count}
              label={proofLabel(event, count)}
              avatars={guest ? [] : avatars}
            />
            {left !== null && left > 0 && !isPast && (
              <span className="text-[13px] text-td-muted">Осталось {pluralSeats(left)}</span>
            )}
          </div>
        }
        action={
          <>
            {isPast && isGoing && (
              <button
                type="button"
                onClick={() => setPhotoDialogOpen(true)}
                className="inline-flex h-11 shrink-0 items-center whitespace-nowrap rounded-full border border-td-line px-4 text-[13px] font-semibold text-td-dark transition hover:border-td-teal/60"
              >
                Добавить фото
              </button>
            )}
            {!isPending && !isPast && (
              <EventAction event={event} count={count} isGoing={isGoing} compact />
            )}
          </>
        }
      />
      <ActivityReportDialog
        open={photoDialogOpen}
        onOpenChange={setPhotoDialogOpen}
        sourceType="event"
        sourceId={event.id}
        title={`Фото с «${event.title}»`}
      />
    </>
  );
}

function EventDialog({
  event,
  count,
  isGoing,
  participants,
  avatars,
  canSeeParticipants,
  canEdit,
  canModerate,
  moderating,
  onModerate,
  onClose,
}: {
  event: EventRow | null;
  count: number;
  isGoing: boolean;
  participants: ProfileMini[];
  avatars: ProofAvatar[];
  canSeeParticipants: boolean;
  canEdit: boolean;
  canModerate: boolean;
  moderating: boolean;
  onModerate: (status: "published" | "rejected") => void;
  onClose: () => void;
}) {
  const [allParticipantsOpen, setAllParticipantsOpen] = useState(false);
  useBodyScrollLock(!!event || allParticipantsOpen);
  const { guest } = useGuest();
  const description = guest ? sanitizePublicDescription(event?.description) : event?.description;
  const date = event?.event_date ? new Date(event.event_date) : null;
  const tz = "Europe/Istanbul";
  const weekday = date?.toLocaleDateString("ru-RU", { weekday: "short", timeZone: tz }) ?? "";
  const dateLabel = (iso: string) => new Date(iso).toLocaleDateString("ru-RU", { day: "numeric", month: "long", timeZone: tz });
  const time = date?.toLocaleTimeString("ru-RU", { hour: "2-digit", minute: "2-digit", timeZone: tz });
  const multiDay = !!event?.event_date && !!event.event_end_date &&
    dateLabel(event.event_date) !== dateLabel(event.event_end_date);
  const location = parseLocationDetails(event?.location_details ?? null);

  return (
    <Dialog
      open={!!event}
      onOpenChange={(o) => {
        if (!o) onClose();
      }}
    >
      <DialogContent onCloseAutoFocus={(e) => e.preventDefault()} className="grid-cols-[minmax(0,1fr)] max-h-[90vh] max-w-[calc(100vw-2rem)] overflow-x-hidden overflow-y-auto sm:max-w-lg">
        {event && (
          <>
            <DialogHeader className="min-w-0">
              <div className="flex min-w-0 items-start gap-2 pr-6">
                <DialogTitle className="min-w-0 flex-1 break-words text-left [overflow-wrap:anywhere]">
                  {event.title}
                </DialogTitle>
                {canEdit && (
                  <Button
                    asChild
                    variant="outline"
                    size="sm"
                    className="h-8 shrink-0 px-2.5 text-xs"
                  >
                    <Link to="/events/$eventId/edit" params={{ eventId: event.id }}>
                      Редактировать
                    </Link>
                  </Button>
                )}
              </div>
              <EventBadges event={event} className="mt-1" />
              <AegisBadge
                circleId={guest ? undefined : (event.circle_id ?? null)}
                eventId={event.id}
                className="mt-1 self-start"
              />
              <DialogDescription className="text-left">
                {event.status === "pending" && (
                  <span className="mr-2 rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold uppercase text-amber-700">
                    На модерации
                  </span>
                )}
                <span className={isCityEvent(event) ? "sr-only" : undefined}>Событие сообщества</span>
              </DialogDescription>
            </DialogHeader>

            <section aria-label="Главное" className="flex min-w-0 items-start gap-3 rounded-2xl border border-td-teal/30 bg-td-teal/5 p-4">
              {date && (
                <div className="flex h-14 w-14 shrink-0 flex-col items-center justify-center rounded-lg bg-td-teal/10 text-td-dark">
                  <span className="text-[10px] uppercase leading-3">{date.toLocaleDateString("ru-RU", { month: "short", timeZone: tz })}</span>
                  <span className="text-2xl font-bold leading-7">{date.toLocaleDateString("ru-RU", { day: "numeric", timeZone: tz })}</span>
                  <span className="text-[10px] uppercase leading-3">{weekday}</span>
                </div>
              )}
              <div className="min-w-0 flex-1 space-y-1 break-words [overflow-wrap:anywhere]">
                <div className="text-lg font-bold leading-snug text-td-dark">
                  {event.event_date ? (
                    time === "00:00" ? <span className="font-normal text-td-muted">время уточняется</span> : time
                  ) : "Дата уточняется"}
                </div>
                {multiDay && event.event_end_date && (
                  <div className="text-xs text-td-muted">до {dateLabel(event.event_end_date)}</div>
                )}
                {location.mapUrl ? (
                  <a
                    href={location.mapUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex min-w-0 items-start text-base font-semibold text-td-dark hover:text-td-teal"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <span className="line-clamp-2 min-w-0 flex-1">
                      <MapPin className="mr-1 inline h-4 w-4" aria-hidden />{event.city}
                      {location.text && ` · ${location.text}`}
                    </span>
                    <span className="ml-1 shrink-0 text-td-teal" aria-hidden>→</span>
                  </a>
                ) : (
                  <div className="text-base font-semibold text-td-dark">
                    <span className="line-clamp-2 min-w-0">
                      <MapPin className="mr-1 inline h-4 w-4" aria-hidden />{event.city}
                      {location.text && ` · ${location.text}`}
                    </span>
                  </div>
                )}
              </div>
            </section>

            <CoverOrPlaceholder
              url={event.cover_image_url}
              title={event.title}
              className="aspect-square w-full rounded-lg"
            />

            {description && (
              <Section title="О событии">
                <p className="whitespace-pre-wrap break-words text-sm text-td-dark [overflow-wrap:anywhere]">
                  {description}
                </p>
              </Section>
            )}

            {!isCityEvent(event) && isPaidEvent(event) && <SignupPaymentSection event={event} />}

            <Section icon={<UserIcon className="h-4 w-4" />} title="Организатор">
              <OrganizerLines event={event} size={32} />
              {isCityEvent(event) && (
                <p className="mt-2 text-[13px] text-td-muted">
                  Билеты и изменения программы смотри у организатора
                </p>
              )}
            </Section>

            {!canSeeParticipants ? (
              count > 0 || (!isPaidEvent(event) && event.max_attendees) ? (
                <div className="flex min-w-0 flex-col gap-1 rounded-lg bg-td-soft/60 p-3">
                  <SocialProof
                    count={count}
                    label={proofLabel(event, count)}
                    avatars={guest ? [] : avatars}
                  />
                  {!isPaidEvent(event) && seatsLeft(event.max_attendees, count) !== null && (
                    <span className="text-[13px] text-td-muted">
                      {seatsLeft(event.max_attendees, count) === 0
                        ? "Мест нет"
                        : `Осталось ${pluralSeats(seatsLeft(event.max_attendees, count)!)}`}
                    </span>
                  )}
                </div>
              ) : null
            ) : participants.length > 0 ? (
              <Section
                icon={<Users className="h-4 w-4" />}
                title={`Участники — ${seatsLabel(event, count)}`}
              >
                <>
                  <div className="flex flex-wrap gap-2">
                    {participants.slice(0, 6).map((p) => (
                      <div
                        key={p.user_id}
                        className="flex min-w-0 max-w-full items-center gap-1.5 rounded-full border border-td-line bg-white py-1 pl-2 pr-1 text-xs text-td-dark"
                      >
                        <Link
                          to="/people/$userId"
                          params={{ userId: p.user_id }}
                          onClick={() => onClose()}
                          className="flex min-w-0 items-center gap-2 hover:text-td-teal"
                        >
                          <AvatarSmall p={p} size={20} />
                          <span className="min-w-0 break-words [overflow-wrap:anywhere]">
                            {p.full_name}
                          </span>
                        </Link>
                        <SaveContactButton
                          targetUserId={p.user_id}
                          fullName={p.full_name}
                          className="h-7 w-7 border-transparent"
                        />
                      </div>
                    ))}
                  </div>

                  {participants.length > 6 && (
                    <button
                      type="button"
                      onClick={() => setAllParticipantsOpen(true)}
                      className="mt-2 text-xs font-semibold text-td-teal underline underline-offset-2"
                    >
                      {`Показать всех участников (${participants.length})`}
                    </button>
                  )}
                </>
              </Section>
            ) : !isPaidEvent(event) && event.max_attendees ? (
              <p className="rounded-lg bg-td-soft/60 p-3 text-sm text-td-muted">
                {seatsLabel(event, count)}
              </p>
            ) : null}

            {canEdit && isPaidEvent(event) && (
              <PaymentsSection eventId={event.id} onClose={onClose} />
            )}

            <Dialog open={allParticipantsOpen} onOpenChange={setAllParticipantsOpen}>
              <DialogContent className="max-w-[calc(100vw-2rem)] overflow-x-hidden sm:max-w-md">
                <DialogHeader>
                  <DialogTitle className="text-left">
                    Участники — {pluralParticipants(participants.length)}
                  </DialogTitle>
                  <DialogDescription className="break-words text-left [overflow-wrap:anywhere]">
                    {event.title}
                  </DialogDescription>
                </DialogHeader>
                <div className="-mx-2 max-h-[60vh] space-y-1 overflow-y-auto px-2">
                  {participants.map((p) => (
                    <div
                      key={p.user_id}
                      className="flex min-w-0 items-center gap-2 rounded-xl px-2 py-2 hover:bg-td-soft/70"
                    >
                      <Link
                        to="/people/$userId"
                        params={{ userId: p.user_id }}
                        onClick={() => {
                          setAllParticipantsOpen(false);
                          onClose();
                        }}
                        className="flex min-w-0 flex-1 items-center gap-3"
                      >
                        <AvatarSmall p={p} size={36} />
                        <span className="truncate text-sm text-td-dark">{p.full_name}</span>
                      </Link>
                      <SaveContactButton targetUserId={p.user_id} fullName={p.full_name} />
                    </div>
                  ))}
                </div>
              </DialogContent>
            </Dialog>

            <DialogFooter className="sticky bottom-0 z-10 flex flex-col gap-2 rounded-2xl border border-td-line bg-background/95 p-2 shadow-lg backdrop-blur sm:flex-col sm:space-x-0">
              {canModerate && (
                <div className="flex flex-wrap items-center justify-end gap-2">
                  <Button
                    size="sm"
                    disabled={moderating}
                    onClick={() => onModerate("published")}
                    className="shrink-0 bg-td-teal text-white hover:bg-td-teal/90"
                  >
                    Опубликовать
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={moderating}
                    onClick={() => onModerate("rejected")}
                    className="shrink-0"
                  >
                    Отклонить
                  </Button>
                </div>
              )}
              <div className="flex w-full min-w-0 items-center gap-2">
                <ShareEventButton eventId={event.id} title={event.title} variant="square" />
                {event.status === "published" && (
                  <div className="min-w-0 flex-1">
                    <EventAction event={event} count={count} isGoing={isGoing} block />
                  </div>
                )}
              </div>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function shortCode(id: string): string {
  return id.slice(-6).toUpperCase();
}

type PaymentRow = {
  id: string;
  user_id: string;
  status: string;
  profile: ProfileMini | null;
};

function PaymentsSection({ eventId, onClose }: { eventId: string; onClose: () => void }) {
  const qc = useQueryClient();

  const { data: rows = [], isLoading } = useQuery({
    queryKey: ["events", "payments", eventId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("event_rsvps")
        .select("id, user_id, status, created_at")
        .eq("event_id", eventId)
        .in("status", ["pending_payment", "paid"])
        .order("created_at", { ascending: true });
      if (error) throw error;
      const ids = [...new Set((data ?? []).map((r) => r.user_id))];
      const profiles = ids.length
        ? ((
            await supabase
              .from("profiles")
              .select("user_id, full_name, photo_url, username")
              .in("user_id", ids)
          ).data ?? [])
        : [];
      const byUid = new Map<string, ProfileMini>();
      for (const pr of profiles) byUid.set(pr.user_id, pr as ProfileMini);
      return (data ?? []).map((r) => ({
        id: r.id,
        user_id: r.user_id,
        status: r.status,
        profile: byUid.get(r.user_id) ?? null,
      })) as PaymentRow[];
    },
  });

  const markPaid = useMutation({
    mutationFn: async (rsvpId: string) => {
      const { error } = await supabase
        .from("event_rsvps")
        .update({ status: "paid" })
        .eq("id", rsvpId);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["events"] });
      toast.success("Оплата отмечена");
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const pending = rows.filter((r) => r.status === "pending_payment");
  const paid = rows.filter((r) => r.status === "paid");

  if (!isLoading && rows.length === 0) return null;

  const renderRow = (r: PaymentRow, withButton: boolean) => (
    <div key={r.id} className="flex items-center gap-2 rounded-xl bg-white px-2 py-2">
      {r.profile ? (
        <Link
          to="/people/$userId"
          params={{ userId: r.user_id }}
          onClick={() => onClose()}
          className="flex min-w-0 flex-1 items-center gap-2"
        >
          <AvatarSmall p={r.profile} size={28} />
          <span className="truncate text-sm text-td-dark">{r.profile.full_name}</span>
        </Link>
      ) : (
        <span className="min-w-0 flex-1 truncate text-sm text-td-muted">Резидент</span>
      )}
      <span className="shrink-0 rounded-full bg-td-soft px-2 py-0.5 font-mono text-[10px] text-td-muted">
        {shortCode(r.id)}
      </span>
      {withButton && (
        <Button
          size="sm"
          variant="outline"
          className="shrink-0"
          disabled={markPaid.isPending}
          onClick={() => markPaid.mutate(r.id)}
        >
          Отметить оплаченным
        </Button>
      )}
    </div>
  );

  return (
    <Section title="Оплаты">
      {isLoading ? (
        <p className="text-sm text-td-muted">Загружаем…</p>
      ) : (
        <div className="space-y-3">
          <div>
            <div className="mb-1 text-xs font-semibold uppercase text-amber-600">
              Ожидают оплаты ({pending.length})
            </div>
            {pending.length === 0 ? (
              <p className="text-xs text-td-muted">Никого не ждём.</p>
            ) : (
              <div className="space-y-1">{pending.map((r) => renderRow(r, true))}</div>
            )}
          </div>
          <div>
            <div className="mb-1 text-xs font-semibold uppercase text-td-teal">
              Оплатили ({paid.length})
            </div>
            {paid.length === 0 ? (
              <p className="text-xs text-td-muted">Пока никто.</p>
            ) : (
              <div className="space-y-1">{paid.map((r) => renderRow(r, false))}</div>
            )}
          </div>
        </div>
      )}
    </Section>
  );
}

function Section({
  icon,
  title,
  children,
}: {
  icon?: React.ReactNode;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div className="min-w-0 rounded-lg bg-td-soft/60 p-3">
      <div className="mb-1 flex min-w-0 items-center gap-1 text-xs font-semibold uppercase text-td-muted">
        {icon}
        <span>{title}</span>
      </div>
      <div className="min-w-0 break-words text-sm text-td-dark [overflow-wrap:anywhere]">
        {children}
      </div>
    </div>
  );
}

function RsvpButton({
  eventId,
  isGoing,
  full,
  sizeCls,
  cityEvent = false,
}: {
  eventId: string;
  isGoing: boolean;
  full: boolean;
  sizeCls: string;
  cityEvent?: boolean;
}) {
  const [confirmOpen, setConfirmOpen] = useState(false);
  const { user } = useAuth();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const mutation = useMutation({
    mutationFn: async () => {
      if (!user) throw new Error("Требуется вход");
      if (isGoing) {
        const { error } = await supabase
          .from("event_rsvps")
          .delete()
          .eq("event_id", eventId)
          .eq("user_id", user.id);
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from("event_rsvps")
          .upsert(
            { event_id: eventId, user_id: user.id, status: "going" },
            { onConflict: "event_id,user_id" },
          );
        if (error) throw error;
        // fire-and-forget: письмо-подтверждение не должно ломать UX
        try {
          void supabase.functions.invoke("send-event-rsvp-confirmation", {
            body: { eventId, userId: user.id },
          });
        } catch (e) {
          console.error("Не удалось отправить подтверждение записи", e);
        }
      }
    },
    onSuccess: async () => {
      qc.invalidateQueries({ queryKey: ["events"] });
      qc.invalidateQueries({ queryKey: ["home", "next-event"] });
      if (isGoing) {
        toast.success(cityEvent ? "Отметка убрана" : "Запись отменена");
        return;
      }
      if (cityEvent) {
        toast.success("Отметили: ты идёшь");
        return;
      }

      const { data } = await supabase
        .from("profiles")
        .select("telegram")
        .eq("user_id", user?.id ?? "")
        .maybeSingle();
      if (data?.telegram?.trim()) {
        toast.success("Ты записан(а). Организатор получит уведомление и свяжется с тобой");
      } else {
        toast("Добавь Telegram в профиль — так организатор сможет с тобой связаться", {
          action: {
            label: "Добавить",
            onClick: () => navigate({ to: "/profile" }),
          },
        });
      }
    },
    onError: (e: Error) => toast.error(e.message),
  });

  if (isGoing) {
    return (
      <>
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            setConfirmOpen(true);
          }}
          disabled={mutation.isPending}
          className={`inline-flex shrink-0 items-center gap-1 whitespace-nowrap rounded-full border border-td-teal bg-td-teal/10 font-semibold text-td-dark transition hover:bg-td-teal/20 disabled:opacity-50 ${sizeCls}`}
        >
          Ты идёшь <Check className="h-4 w-4" />
        </button>
        <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
          <AlertDialogContent className="max-w-sm rounded-2xl" onClick={(e) => e.stopPropagation()}>
            <AlertDialogHeader>
              <AlertDialogTitle>{cityEvent ? "Убрать отметку?" : "Отменить запись?"}</AlertDialogTitle>
              <AlertDialogDescription>
                {cityEvent
                  ? "Отметить снова можно в любой момент."
                  : "Организатор увидит, что ты не придёшь. Записаться снова можно в любой момент."}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>{cityEvent ? "Оставить" : "Не отменять"}</AlertDialogCancel>
              <AlertDialogAction onClick={() => mutation.mutate()}>
                {cityEvent ? "Убрать отметку" : "Отменить запись"}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </>
    );
  }

  if (cityEvent) {
    return (
      <>
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            setConfirmOpen(true);
          }}
          disabled={mutation.isPending}
          className={`inline-flex shrink-0 items-center whitespace-nowrap rounded-full bg-td-teal font-semibold text-white hover:bg-td-teal/90 disabled:opacity-50 ${sizeCls}`}
        >
          Я иду
        </button>
        <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
          <AlertDialogContent className="max-w-sm rounded-2xl" onClick={(e) => e.stopPropagation()}>
            <AlertDialogHeader>
              <AlertDialogTitle>Отметить, что ты идёшь?</AlertDialogTitle>
              <AlertDialogDescription>
                Отметка видна сообществу вместе с твоей аватаркой. Запись и билеты оформляются на сайте организатора.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Не сейчас</AlertDialogCancel>
              <AlertDialogAction onClick={() => mutation.mutate()}>Отметить</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </>
    );
  }

  if (full) {
    return (
      <button
        type="button"
        disabled
        className={`inline-flex shrink-0 cursor-not-allowed items-center whitespace-nowrap rounded-full bg-td-soft font-semibold text-td-muted ${sizeCls}`}
      >
        Мест нет
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        mutation.mutate();
      }}
      disabled={mutation.isPending}
      className={`inline-flex shrink-0 items-center whitespace-nowrap rounded-full bg-td-teal font-semibold text-white transition hover:bg-td-teal/90 disabled:opacity-50 ${sizeCls}`}
    >
      Я иду
    </button>
  );
}
